import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface UseTabHistoryOptions {
  initialTab: string;
}

export const useTabHistory = ({ initialTab }: UseTabHistoryOptions) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = searchParams.get('tab') || initialTab;
  const navigate = useNavigate();
  const tabHistoryRef = useRef<string[]>([initialTab]);

  useEffect(() => {
    const handlePopState = () => {
      const urlTab = new URLSearchParams(window.location.search).get('tab') || initialTab;
      const history = tabHistoryRef.current;
      
      // If we're going back to a tab that's in our history, remove tabs after it
      const tabIndex = history.indexOf(urlTab);
      if (tabIndex !== -1) {
        tabHistoryRef.current = history.slice(0, tabIndex + 1);
      } else if (history.length > 1) {
        // If not in history and we have history, go to previous page
        navigate(-1);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [navigate, initialTab]);

  const changeTab = (newTab: string) => {
    if (newTab !== currentTab) {
      tabHistoryRef.current.push(newTab);
      setSearchParams({ tab: newTab });
    }
  };

  return {
    currentTab,
    changeTab,
  };
};
